import { CONTACT_BOOK_MODES } from "../../@types/sharedSchema";
import { ORG_BOOK_KEY_PREFIX } from "./contactDocumentStore";
import type { ContactBookMode } from "../../@types/sharedSchema";
import type { REST_schema } from "../../@types/sharedSchema";

/**
 * WHICH BOOK THE SIGNED-IN USER'S CONTACTS LIVE IN — the pure half of `useContactBook`.
 *
 * ── The one fact ────────────────────────────────────────────────────────────────────
 *
 * A realtor's contacts live under a BOOK KEY, the mirror's `owner_user_id`: their own email
 * for a personal book, `org:<orgId>` for an agency that switched to the pooled book (the
 * server's `docs/crm/contacts-postgres-authority-plan.md`, D4 — one record per person for
 * the whole office, "my contacts" a holder list beside it). The dashboard used to assume the
 * two were the same thing and keyed every document store, every signal listener and every
 * "is this row mine" comparison on `user.email`. For a pooled member that key names a book
 * that no longer exists.
 *
 * So the server says which book it is (`GET /contacts/book`, zero I/O there), ONE module
 * holds the answer (`contactBookStore`), and everything that used to pass the email as a
 * book key passes `useContactBook().bookKey` instead. For every personal book — the whole
 * fleet today — the key IS the email, so nothing those readers do changes.
 *
 * ── The modes ──────────────────────────────────────────────────────────────────────
 *
 *   personal    the default; absent on the org document
 *   shared      the office's one book is live
 *   migrating   the switch INTO the pooled book is running: every write to the book is
 *               refused with a retry message (a 503 `CRM_BOOK_BUSY`), never queued
 *   reversing   the switch back OUT is running — the same refusal
 *
 * Nothing here decides access. The server resolves the scope from `dashboard-orgs` on every
 * request; what this module decides is which KEY a reader addresses and which LABELS a
 * screen paints.
 */

export type ContactBook = REST_schema.CrmContactBookResponse;

/** A pooled agency's book key starts with this — ONE definition, the document store's. */
export { ORG_BOOK_KEY_PREFIX };

export const isOrgBookKey = (bookKey: string | null | undefined): boolean =>
  typeof bookKey === "string" && bookKey.startsWith(ORG_BOOK_KEY_PREFIX);

/** The agency behind an `org:` key; null for a personal key. */
export const orgIdOfBookKey = (bookKey: string | null | undefined): string | null =>
  isOrgBookKey(bookKey) ? (bookKey as string).slice(ORG_BOOK_KEY_PREFIX.length) : null;

/** A mode the server's registry would list — the three that are not `personal`. */
export const isPooledBookMode = (mode: ContactBookMode | null | undefined): boolean =>
  mode === "shared" || mode === "migrating" || mode === "reversing";

/** The switch is running: writes are refused server-side, so the screen withholds them. */
export const isBookBusy = (mode: ContactBookMode | null | undefined): boolean =>
  mode === "migrating" || mode === "reversing";

/** A server answer with a mode this build knows; anything else is read as `personal` (the narrow answer). */
export const toContactBook = (raw: unknown): ContactBook | null => {
  const data = raw as Partial<ContactBook> | null | undefined;
  if (!data || typeof data.bookKey !== "string" || !data.bookKey.trim()) return null;
  const mode = (CONTACT_BOOK_MODES as readonly string[]).includes(String(data.mode))
    ? (data.mode as ContactBookMode)
    : "personal";
  const orgId = typeof data.orgId === "string" && data.orgId ? data.orgId : null;
  return { bookKey: data.bookKey.trim(), mode, orgId };
};

/**
 * The key a reader addresses the signed-in user's OWN book with. The server's answer when it
 * is in; the email until then — which is the right key for every personal book and a
 * one-frame placeholder for a pooled member (the readers re-key the moment the answer lands).
 *
 * ⚠️ For a reader whose address must be RIGHT rather than merely plausible — the document
 * store, a signal listener — use the strict `bookKey` (null while unknown) and wait.
 */
export const ownBookKey = (book: ContactBook | null, email: string | null | undefined): string | null =>
  book?.bookKey ?? (email ? email : null);

/**
 * The key a contact's DOCUMENTS tab addresses its book with (`documentsData.ts`). The
 * `documents` subcollection is the one thing under a contact the browser still reads and
 * writes directly, so its path has to be right, not plausible:
 *
 *   a pooled member     the office's `org:` key — the files live beside the office's one
 *                       record, where every member of the agency reads them
 *   everybody else      the signed-in email EXACTLY as Firebase Auth spells it, which is
 *                       the path this tab has always used. Not the server's `bookKey`: for
 *                       a personal book that is the same address folded, and a document
 *                       path is case-sensitive — a realtor whose address is spelled with a
 *                       capital would find an empty tab where their files were.
 *
 * `book` is null when the store has no answer (a failed ask, a cold browser whose wait ran
 * out): the email, which is right for every personal book.
 */
export const documentsBookKey = (book: Pick<ContactBook, "bookKey"> | null, authEmail: string): string =>
  book && isOrgBookKey(book.bookKey) ? book.bookKey : authEmail;

/**
 * What a writer is told while the switch runs. MIRRORED from the server's
 * `pooledBookRules.ts` (`POOLED_BOOK_BUSY_MESSAGE`): a refused write carries the same
 * sentence on its 503, and the CRM page paints it BEFORE a write is attempted so a realtor
 * is not invited to click something that will be refused.
 */
export const POOLED_BOOK_BUSY_MESSAGE = "מאגר המשרד בתהליך איחוד, נסו שוב בעוד כמה דקות";

/** The `CRM_BOOK_BUSY` refusal's code, as the contacts controllers answer it. */
export const CRM_BOOK_BUSY_CODE = "CRM_BOOK_BUSY";

/**
 * The CRM page's two tab titles. On the pooled book the second tab is the office's ONE set
 * of customers, so it is named for them ("לקוחות המשרד"); the first keeps its name on both
 * books, because to the realtor it IS the same thing as before — their own customers (on
 * the pooled book, the ones they hold). The personal book keeps the labels it always had,
 * byte for byte. The admin's pool window quotes both names from here: it tells the admin
 * where to find their customers after the merge, so a rename here is a rename there.
 */
export const crmViewTabTitles = (mode: ContactBookMode | null | undefined): { mine: string; org: string } =>
  isPooledBookMode(mode) ? { mine: "הלקוחות שלי", org: "לקוחות המשרד" } : { mine: "הלקוחות שלי", org: "מאגר המשרד" };

/**
 * Is a pooled record on THIS viewer's list. `holderIds` is what the server stamps on every
 * `org:` row and document (lowercased emails); a personal row carries `[]` and is the
 * viewer's own by construction, which is the `true` branch.
 */
export const viewerHoldsContact = (
  row: { bookKey?: string | null; ownerUserId?: string | null; holderIds?: readonly string[] | null },
  viewerUserId: string | null | undefined
): boolean => {
  const key = row.bookKey ?? row.ownerUserId ?? null;
  if (!isOrgBookKey(key)) return true;
  const viewer = (viewerUserId ?? "").trim().toLowerCase();
  if (!viewer) return false;
  return (row.holderIds ?? []).some((holder) => holder.trim().toLowerCase() === viewer);
};
